import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CAPABILITIES = [
  {
    icon: 'ri-brain-line',
    labelKey: 'about.core.cap.customAi.label',
    textKey: 'about.core.cap.customAi.text',
    angle: -90,
    color: 'accent',
  },
  {
    icon: 'ri-code-s-slash-line',
    labelKey: 'about.core.cap.software.label',
    textKey: 'about.core.cap.software.text',
    angle: -30,
    color: 'secondary',
  },
  {
    icon: 'ri-database-2-line',
    labelKey: 'about.core.cap.data.label',
    textKey: 'about.core.cap.data.text',
    angle: 30,
    color: 'accent',
  },
  {
    icon: 'ri-settings-4-line',
    labelKey: 'about.core.cap.automation.label',
    textKey: 'about.core.cap.automation.text',
    angle: 90,
    color: 'secondary',
  },
  {
    icon: 'ri-git-merge-line',
    labelKey: 'about.core.cap.integrations.label',
    textKey: 'about.core.cap.integrations.text',
    angle: 150,
    color: 'accent',
  },
  {
    icon: 'ri-shield-check-line',
    labelKey: 'about.core.cap.security.label',
    textKey: 'about.core.cap.security.text',
    angle: 210,
    color: 'secondary',
  },
];

export default function AboutCoreDiagram() {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="relative w-full aspect-square max-w-[420px] mx-auto select-none">
      {/* Animated orbital rings */}
      <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <linearGradient id="orbitGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#7057FF" stopOpacity="0.4" />
            <stop offset="50%" stopColor="#22D3C5" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#7057FF" stopOpacity="0.4" />
          </linearGradient>
        </defs>

        {/* Outer dashed ring */}
        <circle
          cx="200"
          cy="200"
          r="185"
          fill="none"
          stroke="url(#orbitGrad)"
          strokeWidth="1"
          strokeDasharray="3 9"
          className={isVisible ? 'animate-[spin_50s_linear_infinite]' : ''}
          style={{ transformOrigin: '200px 200px' }}
        />
        {/* Mid ring */}
        <circle
          cx="200"
          cy="200"
          r="150"
          fill="none"
          stroke="#7057FF"
          strokeWidth="0.5"
          opacity="0.3"
          strokeDasharray="1 7"
          className={isVisible ? 'animate-[spin_35s_linear_infinite_reverse]' : ''}
          style={{ transformOrigin: '200px 200px' }}
        />
        {/* Inner ring */}
        <circle
          cx="200"
          cy="200"
          r="118"
          fill="none"
          stroke="#22D3C5"
          strokeWidth="0.5"
          opacity="0.35"
          className={isVisible ? 'animate-[spin_25s_linear_infinite]' : ''}
          style={{ transformOrigin: '200px 200px' }}
        />

        {/* Orbiting particles on outer ring */}
        {isVisible &&
          [0, 1, 2, 3].map((i) => (
            <g key={`p-${i}`}>
              <circle r="3" fill="#22D3C5" opacity="0.8">
                <animateMotion
                  dur={`${14 + i * 3}s`}
                  repeatCount="indefinite"
                  path="M 200 15 A 185 185 0 1 1 199.9 15"
                />
              </circle>
            </g>
          ))}
        {isVisible &&
          [0, 1, 2].map((i) => (
            <g key={`q-${i}`}>
              <circle r="2" fill="#7057FF" opacity="0.7">
                <animateMotion
                  dur={`${18 + i * 4}s`}
                  repeatCount="indefinite"
                  path="M 200 15 A 185 185 0 1 0 199.9 15"
                  begin={`${i * 1.5}s`}
                />
              </circle>
            </g>
          ))}
      </svg>

      {/* Central core */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <div
          className={`relative w-24 h-24 md:w-32 md:h-32 transition-all duration-1000 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
          }`}
        >
          {/* Glow */}
          <div className="absolute inset-0 rounded-full bg-accent-500/20 blur-2xl animate-[morphPulse_3s_ease-in-out_infinite]" />
          {/* Hexagon */}
          <div
            className="absolute inset-0 flex items-center justify-center animate-[corePulse_3s_ease-in-out_infinite]"
            style={{
              clipPath:
                'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
            }}
          >
            <div className="absolute inset-0 bg-primary-500" />
            <div className="absolute inset-2 bg-gradient-to-br from-primary-500 to-primary-700" />
          </div>
          {/* Inner label / logo */}
          <div className="absolute inset-0 flex items-start justify-center pt-5 md:pt-7">
            <img
              src="https://storage.helloreaddy.io/project_files/bb209c14-9fa9-4034-a099-af83f1204fe6/ed6231ec-ad64-40b6-9b06-9ed759a36c3f_compressed_ese.webp"
              alt="MorphenIQ"
              className="w-16 md:w-20 h-auto object-contain pointer-events-none"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </div>
        </div>
      </div>

      {/* Capability nodes orbiting the core */}
      {CAPABILITIES.map((cap, i) => {
        const rad = (cap.angle * Math.PI) / 180;
        const radius = 44; // % from center
        const x = 50 + radius * Math.cos(rad);
        const y = 50 + radius * Math.sin(rad);

        return (
          <div
            key={cap.labelKey}
            className="absolute z-20"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <div
              className={`flex flex-col items-center text-center transition-all duration-700 ${
                isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
              }`}
              style={{ transitionDelay: `${300 + i * 120}ms` }}
            >
              {/* Icon bubble */}
              <div
                className={`w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center border mb-1.5 bg-background-50 shadow-sm transition-all duration-500 hover:scale-110 cursor-pointer ${
                  cap.color === 'accent'
                    ? 'border-accent-200 text-accent-600'
                    : 'border-secondary-300 text-secondary-700'
                }`}
              >
                <i className={`${cap.icon} text-base md:text-lg`} />
              </div>
              {/* Label */}
              <span className="text-[11px] md:text-xs font-heading font-semibold text-foreground-900 whitespace-nowrap">
                {t(cap.labelKey)}
              </span>
              <span className="text-[9px] md:text-[10px] text-foreground-500 whitespace-nowrap">
                {t(cap.textKey)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}